import { useMutation } from '@apollo/client'
import { Box, FormModal, Link, Text } from '@chaine/keychaine'
import React, { useState } from 'react'
import { SubmitHandler } from 'react-hook-form'

import { Toast } from '../../../_shared/components/toast'
import { validations } from '../../../_utils/validations/validations'
import { AppModalProps } from '../components/apps-data'
import { ENABLE_GEO_TAB_APP } from '../data/mutations'

/**
 * Saves GeoTab app's username, database and password.
 * @param props.app the app
 * @param props.isOpen sets the modal to open or closed
 * @param props.onClose closes the modal
 */
const GeoTabModal = React.memo((props: AppModalProps) => {
  const { isOpen, onClose, appType, refetchApps } = props

  const [formError, setFormError] = useState('')

  const { showToast } = Toast()

  const [enableGeoTabApp, { loading: enableAppLoading }] = useMutation<{
    enableGeoTabAppCredentials: { appType: string; databaseName: string; password: string; username: string }
  }>(ENABLE_GEO_TAB_APP, {
    onCompleted: () => {
      showToast({
        status: 'success',
        title: `GeoTab app is enabled successfully!`
      })
      setFormError('')
      refetchApps()
      closeModal()
    },
    onError: (err) => {
      if (err.message === 'Incorrect login credentials') {
        setFormError('Please enter correct user name or password.')
        showToast({
          status: 'error',
          title: `Please enter correct user name or password`
        })
      } else if (err.message === 'Incorrect database name') {
        setFormError('Please enter correct database name.')
        showToast({
          status: 'error',
          title: `Please enter correct database name`
        })
      } else {
        setFormError(`Something went wrong. Please contact support@chaineapp.com. Error: ${err.message}`)
        showToast({
          status: 'error',
          title: `Something went wrong. Please contact support@chaineapp.com. Error: ${err.message}`
        })
      }
    }
  })

  type FieldValues = { appDatabase?: string; appPassword?: string; appUsername?: string }
  const handleSaveDetails: SubmitHandler<FieldValues> = (event) => {
    const { appUsername, appPassword, appDatabase } = event
    enableGeoTabApp({
      variables: { appType: appType, databaseName: appDatabase, password: appPassword, username: appUsername }
    })
  }

  const closeModal = () => {
    setFormError('')
    onClose()
  }

  const footer = (
    <Box>
      <Text color="red.600">{formError}</Text>
      <Text>
        {`See `}
        <Link
          color="blue.600"
          href="https://handbook.chaineapp.com/product-guide/carriers/connect-eld/elds/geo-tab/"
          target="_blank"
          rel="noopener noreferrer"
        >
          detailed steps here.
        </Link>
      </Text>
    </Box>
  )

  return (
    <>
      <FormModal
        isOpen={isOpen}
        onClose={closeModal}
        title={`Enable ${appType} for Chaine`}
        subtitle={`Enabling this integration requires an Admin geo tab account. Enter your admin username, password and database.`}
        formSubmitHandler={handleSaveDetails}
        buttonText={'Enable'}
        initialValues={{}}
        validations={validations}
        isLoading={enableAppLoading}
        footer={formError ? footer : <></>}
        modalItems={[
          {
            labelText: 'GeoTab admin username',
            name: 'appUsername',

            placeHolder: 'Enter your GeoTab username',
            type: 'input'
          },
          {
            labelText: 'GeoTab admin password',
            name: 'appPassword',

            placeHolder: 'Enter your GeoTab password',
            type: 'input'
          },
          {
            labelText: 'GeoTab database name',
            name: 'appDatabase',

            placeHolder: 'Enter the GeoTab database name',
            type: 'input'
          }
        ]}
      />
    </>
  )
})

GeoTabModal.displayName = 'GeoTabModal'

export { GeoTabModal }
